import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Switch,
  message,
  Space,
  Popconfirm,
  Tag,
  DatePicker,
  Select,
  Card,
  Pagination,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { apiClient } from '../../config/apiClient';
import MainLayout from '../layout/layout';
import dayjs from 'dayjs';
import { LoginContext } from '../../context/loginContext';

// Función para calcular la edad a partir de la fecha de nacimiento
const calcularEdad = (fechaNacimiento) => {
  if (!fechaNacimiento) return null;
  
  const fechaNac = dayjs(fechaNacimiento);
  const hoy = dayjs();
  const edad = hoy.diff(fechaNac, 'year');
  
  return edad >= 0 ? edad : null;
};

const AdministracionAlumnos = () => {
  const { user } = React.useContext(LoginContext);
  const [alumnos, setAlumnos] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingAlumno, setEditingAlumno] = useState(null);
  const [form] = Form.useForm();
  const [formAgregarAlumno] = Form.useForm();
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  const [modalAgregarAlumnoVisible, setModalAgregarAlumnoVisible] = useState(false);
  const [jugadoresClub, setJugadoresClub] = useState([]);
  const [loadingJugadores, setLoadingJugadores] = useState(false);
  const [savingAgregarAlumno, setSavingAgregarAlumno] = useState(false);
  const [filtroJugadorAgregar, setFiltroJugadorAgregar] = useState('');
  const [paginationAgregar, setPaginationAgregar] = useState({ current: 1, pageSize: 10 });
  const [jugadorSeleccionadoAgregar, setJugadorSeleccionadoAgregar] = useState(null);
  const [filtroNombre, setFiltroNombre] = useState('');
  const [filtroCategoria, setFiltroCategoria] = useState(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const normalizarEmail = (email) => (email || '').toString().toLowerCase().trim();

  // Verificar si el usuario es administrador
  const esAdministrador = () => {
    if (!user) {
      console.log('❌ esAdministrador: No hay usuario');
      return false;
    }
    
    // El objeto user puede tener diferentes estructuras:
    // - Del login: usu_tipousuario, tiu_descripcion
    // - Del JWT: tipoUsuario
    const userTipoUsuario = user?.tipoUsuario || user?.usu_tipousuario;
    const userRole = user?.tiu_descripcion || '';
    
    // Verificar por tipoUsuario (1 = ADMINISTRADOR)
    if (userTipoUsuario !== undefined && userTipoUsuario !== null) {
      if (userTipoUsuario === 1 || userTipoUsuario === '1') {
        console.log('✅ esAdministrador: Rol detectado por tipoUsuario = 1');
        return true;
      }
    }
    
    // Verificar por rol de texto
    if (userRole && userRole.toUpperCase() === 'ADMINISTRADOR') {
      console.log('✅ esAdministrador: Rol detectado por tiu_descripcion = ADMINISTRADOR');
      return true;
    }
    
    console.log('❌ esAdministrador: No es administrador', {
      userTipoUsuario,
      userRole,
      user
    });
    return false;
  };

  // Cargar categorías
  const loadCategorias = async () => {
    try {
      const response = await apiClient.get('/categorias');
      if (response.ok) {
        const data = await response.json();
        setCategorias(data.filter(cat => cat.cat_activo));
      }
    } catch (error) {
      console.error('Error al cargar categorías:', error);
    }
  };

  // Cargar jugadores del club (para agregar alumno desde jugador)
  const loadJugadoresClub = async () => {
    setLoadingJugadores(true);
    try {
      const response = await apiClient.get('/club/jugadores');
      if (response.ok) {
        const data = await response.json();
        setJugadoresClub(Array.isArray(data) ? data : []);
      } else {
        setJugadoresClub([]);
      }
    } catch (error) {
      console.error('Error al cargar jugadores:', error);
      setJugadoresClub([]);
    } finally {
      setLoadingJugadores(false);
    }
  };

  // Cargar alumnos con filtros y paginación
  const loadAlumnos = async (nombre = null, categoriaId = null, page = 1, pageSize = 10) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (nombre) params.append('nombre', nombre);
      if (categoriaId) params.append('categoriaId', categoriaId);
      params.append('page', page.toString());
      params.append('pageSize', pageSize.toString());
      
      const queryString = params.toString();
      const url = `/alumnos?${queryString}`;
      
      const response = await apiClient.get(url);
      if (response.ok) {
        const result = await response.json();
        setAlumnos(result.data || []);
        setPagination({
          current: result.page || 1,
          pageSize: result.pageSize || 10,
          total: result.total || 0,
        });
      } else {
        message.error('Error al cargar los alumnos');
      }
    } catch (error) {
      console.error('Error:', error);
      message.error('Error al cargar los alumnos');
    } finally {
      setLoading(false);
    }
  };

  // Manejar cambio en filtro de nombre
  const handleFiltroNombreChange = (e) => {
    const valor = e.target.value;
    setFiltroNombre(valor);
    // Debounce: esperar 500ms después de que el usuario deje de escribir
    clearTimeout(window.filtroNombreTimeout);
    window.filtroNombreTimeout = setTimeout(() => {
      setPagination(prev => ({ ...prev, current: 1 }));
      loadAlumnos(valor || null, filtroCategoria, 1, pagination.pageSize);
    }, 500);
  };

  // Manejar cambio en filtro de categoría
  const handleFiltroCategoriaChange = (value) => {
    setFiltroCategoria(value);
    setPagination(prev => ({ ...prev, current: 1 }));
    loadAlumnos(filtroNombre || null, value || null, 1, pagination.pageSize);
  };

  // Limpiar filtros
  const handleLimpiarFiltros = () => {
    setFiltroNombre('');
    setFiltroCategoria(null);
    setPagination(prev => ({ ...prev, current: 1 }));
    loadAlumnos(null, null, 1, pagination.pageSize);
  };

  // Manejar cambio de página
  const handleTableChange = (newPagination) => {
    setPagination({
      current: newPagination.current,
      pageSize: newPagination.pageSize,
      total: pagination.total,
    });
    loadAlumnos(
      filtroNombre || null,
      filtroCategoria,
      newPagination.current,
      newPagination.pageSize
    );
  };

  useEffect(() => {
    loadCategorias();
    loadAlumnos(null, null, 1, 10);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Abrir modal para agregar alumno (desde jugador del club)
  const handleAgregarAlumno = () => {
    formAgregarAlumno.resetFields();
    setFiltroJugadorAgregar('');
    setPaginationAgregar({ current: 1, pageSize: 10 });
    setJugadorSeleccionadoAgregar(null);
    setModalAgregarAlumnoVisible(true);
    loadJugadoresClub();
  };

  // Guardar alumno desde jugador (jugador + categoría)
  const handleAgregarAlumnoSave = async () => {
    try {
      const values = await formAgregarAlumno.validateFields();
      setSavingAgregarAlumno(true);
      const response = await apiClient.post('/alumnos/desde-jugador', {
        jugadorClub: values.jugadorClub,
        categoria: values.categoria,
      });
      if (response.ok) {
        message.success('Alumno agregado correctamente');
        setModalAgregarAlumnoVisible(false);
        formAgregarAlumno.resetFields();
        setJugadorSeleccionadoAgregar(null);
        setFiltroJugadorAgregar('');
        setPaginationAgregar({ current: 1, pageSize: 10 });
        loadAlumnos(filtroNombre || null, filtroCategoria, pagination.current, pagination.pageSize);
      } else {
        const error = await response.json();
        message.error(error.message || 'Error al agregar alumno');
      }
    } catch (error) {
      if (error.errorFields) return;
      console.error('Error:', error);
      message.error('Error al agregar alumno');
    } finally {
      setSavingAgregarAlumno(false);
    }
  };


  // Abrir modal para editar alumno
  const handleEdit = (record) => {
    setEditingAlumno(record);
    form.setFieldsValue({
      nombre: record.alu_nombre,
      apellido: record.alu_apellido,
      fechaNacimiento: record.alu_fecha_nacimiento ? dayjs(record.alu_fecha_nacimiento) : null,
      sexo: record.alu_sexo,
      email: record.alu_email,
      telefono: record.alu_telefono,
      categoria: record.alu_categoria,
      activo: record.alu_activo,
    });
    setModalVisible(true);
  };

  // Guardar alumno (crear o actualizar)
  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      
      // Convertir fecha de dayjs a string formato YYYY-MM-DD
      const dataToSend = {
        ...values,
        fechaNacimiento: values.fechaNacimiento 
          ? values.fechaNacimiento.format('YYYY-MM-DD')
          : null,
      };
      
      if (editingAlumno) {
        // Actualizar
        const response = await apiClient.put(`/alumnos/${editingAlumno.alu_id}`, dataToSend);
        if (response.ok) {
          message.success('Alumno actualizado correctamente');
          setModalVisible(false);
          loadAlumnos(filtroNombre || null, filtroCategoria, pagination.current, pagination.pageSize);
        } else {
          const error = await response.json();
          message.error(error.message || 'Error al actualizar el alumno');
        }
      } else {
        // Crear
        const response = await apiClient.post('/alumnos', dataToSend);
        if (response.ok) {
          message.success('Alumno creado correctamente');
          setModalVisible(false);
          loadAlumnos(filtroNombre || null, filtroCategoria, pagination.current, pagination.pageSize);
        } else {
          const error = await response.json();
          message.error(error.message || 'Error al crear el alumno');
        }
      }
    } catch (error) {
      console.error('Error:', error);
      if (error.errorFields) {
        // Errores de validación del formulario
        return;
      }
      message.error('Error al guardar el alumno');
    }
  };

  // Eliminar alumno
  const handleDelete = async (id) => {
    try {
      const response = await apiClient.delete(`/alumnos/${id}`);
      if (response.ok) {
        message.success('Alumno eliminado correctamente');
        loadAlumnos(filtroNombre || null, filtroCategoria, pagination.current, pagination.pageSize);
      } else {
        const error = await response.json();
        message.error(error.message || 'Error al eliminar el alumno');
      }
    } catch (error) {
      console.error('Error:', error);
      message.error('Error al eliminar el alumno');
    }
  };

  // Columnas de la tabla
  const columns = [
    {
      title: 'Nombre',
      dataIndex: 'alu_nombre',
      key: 'alu_nombre',
    },
    {
      title: 'Apellido',
      dataIndex: 'alu_apellido',
      key: 'alu_apellido',
    },
    {
      title: 'Email',
      dataIndex: 'alu_email',
      key: 'alu_email',
      render: (email) => email || '-',
    },
    {
      title: 'Teléfono',
      dataIndex: 'alu_telefono',
      key: 'alu_telefono',
      render: (telefono) => telefono || '-',
    },
    {
      title: 'Categoría',
      dataIndex: 'cat_descripcion',
      key: 'cat_descripcion',
      render: (descripcion) => descripcion || '-',
    },
    {
      title: 'Estado',
      dataIndex: 'alu_activo',
      key: 'alu_activo',
      width: 120,
      render: (activo) => (
        <Tag color={activo ? 'green' : 'red'}>
          {activo ? 'Activo' : 'Inactivo'}
        </Tag>
      ),
    },
    {
      title: 'Acciones',
      key: 'acciones',
      width: 200,
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Editar
          </Button>
          <Popconfirm
            title="¿Estás seguro de eliminar este alumno?"
            onConfirm={() => handleDelete(record.alu_id)}
            okText="Sí"
            cancelText="No"
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
            >
              Eliminar
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // Jugadores del club que aún no son alumnos (por email)
  const jugadoresDisponibles = jugadoresClub.filter((j) => {
    const emailJugador = normalizarEmail(j.jcl_email);
    return !alumnos.some((a) => {
      const emailAlumno = normalizarEmail(a.alu_email);
      // Solo deduplicar cuando ambos emails existen.
      if (!emailAlumno || !emailJugador) return false;
      return emailAlumno === emailJugador;
    });
  });

  // Filtro y paginación para el modal Agregar alumno
  const filtroJugadorLower = (filtroJugadorAgregar || '').toLowerCase().trim();
  const jugadoresFiltrados = filtroJugadorLower
    ? jugadoresDisponibles.filter(
        (j) =>
          (j.jcl_nombre || '').toLowerCase().includes(filtroJugadorLower) ||
          (j.jcl_apellido || '').toLowerCase().includes(filtroJugadorLower) ||
          (j.jcl_email || '').toLowerCase().includes(filtroJugadorLower) ||
          (j.jcl_telefono || '').toString().includes(filtroJugadorAgregar.trim())
      )
    : jugadoresDisponibles;
  const totalJugadoresFiltrados = jugadoresFiltrados.length;
  const jugadoresPaginados = jugadoresFiltrados.slice(
    (paginationAgregar.current - 1) * paginationAgregar.pageSize,
    paginationAgregar.current * paginationAgregar.pageSize
  );

  const columnasModalJugadores = [
    { title: 'Nombre', dataIndex: 'jcl_nombre', key: 'jcl_nombre', width: 120, render: (t) => t || '-' },
    { title: 'Apellido', dataIndex: 'jcl_apellido', key: 'jcl_apellido', width: 120, render: (t) => t || '-' },
    { title: 'Email', dataIndex: 'jcl_email', key: 'jcl_email', ellipsis: true, render: (t) => t || '-' },
    { title: 'Teléfono', dataIndex: 'jcl_telefono', key: 'jcl_telefono', width: 130, render: (t) => t || '-' },
  ];

  return (
    <MainLayout>
      <div style={{ padding: '24px' }}>
        <div style={{ marginBottom: 16 }}>
          <h1 style={{ margin: 0, marginBottom: 16, color: '#ffffff', fontSize: isMobile ? '20px' : '24px' }}>Administración de Alumnos</h1>
          <Space wrap style={{ marginBottom: 16, width: '100%' }}>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => loadAlumnos(filtroNombre || null, filtroCategoria, pagination.current, pagination.pageSize)}
              loading={loading}
            >
              {isMobile ? null : 'Actualizar'}
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAgregarAlumno}
            >
              Agregar alumno
            </Button>
          </Space>
          
          {/* Filtros de búsqueda */}
          <div style={{ 
            marginBottom: 16, 
            padding: 16, 
            backgroundColor: '#2d2d2d', 
            borderRadius: 8,
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            gap: 12,
            alignItems: isMobile ? 'stretch' : 'flex-end'
          }}>
            <div style={{ flex: 1, minWidth: isMobile ? '100%' : '200px' }}>
              <label style={{ display: 'block', marginBottom: 4, color: '#ffffff', fontSize: '14px' }}>
                Buscar por nombre
              </label>
              <Input
                placeholder="Ej: valen, longo, valentin..."
                value={filtroNombre}
                onChange={handleFiltroNombreChange}
                allowClear
                style={{ width: '100%' }}
              />
            </div>
            <div style={{ flex: 1, minWidth: isMobile ? '100%' : '200px' }}>
              <label style={{ display: 'block', marginBottom: 4, color: '#ffffff', fontSize: '14px' }}>
                Filtrar por categoría
              </label>
              <Select
                placeholder="Todas las categorías"
                value={filtroCategoria}
                onChange={handleFiltroCategoriaChange}
                allowClear
                style={{ width: '100%' }}
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) =>
                  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
              >
                {categorias.map(cat => (
                  <Select.Option key={cat.cat_id} value={cat.cat_id} label={cat.cat_descripcion}>
                    {cat.cat_descripcion}
                  </Select.Option>
                ))}
              </Select>
            </div>
            {(filtroNombre || filtroCategoria) && (
              <Button
                onClick={handleLimpiarFiltros}
                style={{ minWidth: isMobile ? '100%' : 'auto' }}
              >
                Limpiar Filtros
              </Button>
            )}
          </div>
        </div>

        {isMobile ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {alumnos.map(alumno => (
              <Card
                key={alumno.alu_id}
                hoverable
                style={{
                  borderLeft: '4px solid #1890ff',
                  borderRadius: '8px',
                }}
              >
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 'bold', fontSize: '16px', marginBottom: '8px', color: '#ffffff' }}>
                        {alumno.alu_nombre} {alumno.alu_apellido}
                      </div>
                      <div style={{ fontSize: '13px', color: '#b0b0b0', marginBottom: '4px' }}>
                        Email: {alumno.alu_email || '-'}
                      </div>
                      <div style={{ fontSize: '13px', color: '#b0b0b0', marginBottom: '4px' }}>
                        Teléfono: {alumno.alu_telefono || '-'}
                      </div>
                      <div style={{ fontSize: '13px', color: '#b0b0b0', marginBottom: '4px' }}>
                        Categoría: {alumno.cat_descripcion || '-'}
                      </div>
                    </div>
                    <Tag color={alumno.alu_activo ? 'green' : 'red'}>
                      {alumno.alu_activo ? 'Activo' : 'Inactivo'}
                    </Tag>
                  </div>
                  <Space wrap>
                    <Button
                      type="link"
                      icon={<EditOutlined />}
                      onClick={() => handleEdit(alumno)}
                    >
                      Editar
                    </Button>
                    <Popconfirm
                      title="¿Estás seguro de eliminar este alumno?"
                      onConfirm={() => handleDelete(alumno.alu_id)}
                      okText="Sí"
                      cancelText="No"
                    >
                      <Button
                        type="link"
                        danger
                        icon={<DeleteOutlined />}
                      >
                        Eliminar
                      </Button>
                    </Popconfirm>
                  </Space>
                </div>
              </Card>
            ))}
            {pagination.total > 0 && (
              <div style={{ marginTop: 16, display: 'flex', justifyContent: 'center' }}>
                <Pagination
                  current={pagination.current}
                  pageSize={pagination.pageSize}
                  total={pagination.total}
                  onChange={(page, pageSize) => {
                    setPagination(prev => ({ ...prev, current: page, pageSize }));
                    loadAlumnos(filtroNombre || null, filtroCategoria, page, pageSize);
                  }}
                  onShowSizeChange={(current, size) => {
                    setPagination(prev => ({ ...prev, current: 1, pageSize: size }));
                    loadAlumnos(filtroNombre || null, filtroCategoria, 1, size);
                  }}
                  showSizeChanger
                  pageSizeOptions={['10', '20', '50', '100']}
                  showTotal={(total) => `Total: ${total} alumnos`}
                />
              </div>
            )}
          </div>
        ) : (
          <Table
            columns={columns}
            dataSource={alumnos}
            loading={loading}
            rowKey="alu_id"
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: true,
              showTotal: (total) => `Total: ${total} alumnos`,
              pageSizeOptions: ['10', '20', '50', '100'],
            }}
            onChange={handleTableChange}
          />
        )}

        {/* Modal Agregar alumno (desde jugador del club + categoría) */}
        <Modal
          title="Agregar alumno"
          open={modalAgregarAlumnoVisible}
          onOk={handleAgregarAlumnoSave}
          onCancel={() => {
            setModalAgregarAlumnoVisible(false);
            formAgregarAlumno.resetFields();
            setFiltroJugadorAgregar('');
            setPaginationAgregar({ current: 1, pageSize: 10 });
            setJugadorSeleccionadoAgregar(null);
          }}
          okText="Agregar"
          cancelText="Cancelar"
          width={900}
          confirmLoading={savingAgregarAlumno}
          destroyOnClose
        >
          <p style={{ color: '#b0b0b0', marginBottom: 16 }}>
            Seleccione un jugador del club que aún no sea alumno y asigne la categoría.
          </p>
          <Form form={formAgregarAlumno} layout="vertical">
            <Form.Item
              name="jugadorClub"
              hidden
              rules={[{ required: true, message: 'Seleccione un jugador de la tabla' }]}
            >
              <Input type="hidden" />
            </Form.Item>
            <Form.Item
              name="categoria"
              label="Categoría del nuevo alumno"
              rules={[{ required: true, message: 'Seleccione la categoría' }]}
              style={{ marginBottom: 16 }}
            >
              <Select
                placeholder="Seleccione la categoría"
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) =>
                  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
                style={{ maxWidth: 320 }}
              >
                {categorias.map((cat) => (
                  <Select.Option key={cat.cat_id} value={cat.cat_id} label={cat.cat_descripcion}>
                    {cat.cat_descripcion}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item label="Jugadores disponibles (seleccione uno)">
              <div style={{ marginBottom: 12 }}>
                <Input.Search
                  placeholder="Filtrar por nombre, apellido, email o teléfono..."
                  value={filtroJugadorAgregar}
                  onChange={(e) => {
                    setFiltroJugadorAgregar(e.target.value);
                    setPaginationAgregar((p) => ({ ...p, current: 1 }));
                  }}
                  onSearch={(v) => {
                    setFiltroJugadorAgregar(v);
                    setPaginationAgregar((p) => ({ ...p, current: 1 }));
                  }}
                  allowClear
                  style={{ maxWidth: 400 }}
                  enterButton="Buscar"
                />
              </div>
              {jugadoresClub.length === 0 && !loadingJugadores ? (
                <div style={{ padding: 24, textAlign: 'center', color: '#8c8c8c' }}>
                  No hay jugadores en el club.
                </div>
              ) : jugadoresDisponibles.length === 0 ? (
                <div style={{ padding: 24, textAlign: 'center', color: '#8c8c8c' }}>
                  Todos los jugadores ya son alumnos.
                </div>
              ) : (
                <Table
                  size="small"
                  loading={loadingJugadores}
                  rowKey="jcl_id"
                  dataSource={jugadoresPaginados}
                  columns={columnasModalJugadores}
                  pagination={{
                    current: paginationAgregar.current,
                    pageSize: paginationAgregar.pageSize,
                    total: totalJugadoresFiltrados,
                    showSizeChanger: true,
                    pageSizeOptions: ['5', '10', '20', '50'],
                    showTotal: (total) => `Total: ${total} jugadores`,
                    onChange: (page, pageSize) => setPaginationAgregar({ current: page, pageSize: pageSize || 10 }),
                  }}
                  rowSelection={{
                    type: 'radio',
                    selectedRowKeys: jugadorSeleccionadoAgregar ? [jugadorSeleccionadoAgregar] : [],
                    onChange: (selectedRowKeys, selectedRows) => {
                      const id = selectedRowKeys[0];
                      setJugadorSeleccionadoAgregar(id || null);
                      formAgregarAlumno.setFieldsValue({ jugadorClub: id || undefined });
                    },
                  }}
                  scroll={{ x: 500 }}
                  style={{ marginBottom: 0 }}
                />
              )}
            </Form.Item>
          </Form>
        </Modal>

        {/* Modal para editar alumno */}
        <Modal
          title="Editar Alumno"
          open={modalVisible}
          onOk={handleSave}
          onCancel={() => {
            setModalVisible(false);
            form.resetFields();
          }}
          okText="Guardar"
          cancelText="Cancelar"
          width={600}
        >
          <Form
            form={form}
            layout="vertical"
          >
            <Form.Item
              name="nombre"
              label="Nombre"
              rules={[
                { required: true, message: 'El nombre es requerido' },
                { max: 100, message: 'El nombre no puede exceder 100 caracteres' },
              ]}
            >
              <Input placeholder="Ingrese el nombre" />
            </Form.Item>

            <Form.Item
              name="apellido"
              label="Apellido"
              rules={[
                { required: true, message: 'El apellido es requerido' },
                { max: 100, message: 'El apellido no puede exceder 100 caracteres' },
              ]}
            >
              <Input placeholder="Ingrese el apellido" />
            </Form.Item>

            <Form.Item
              name="fechaNacimiento"
              label="Fecha de Nacimiento"
            >
              <DatePicker
                style={{ width: '100%' }}
                format="DD/MM/YYYY"
                placeholder="Seleccione la fecha de nacimiento"
              />
            </Form.Item>

            <Form.Item
              noStyle
              shouldUpdate={(prevValues, currentValues) => 
                prevValues.fechaNacimiento !== currentValues.fechaNacimiento
              }
            >
              {({ getFieldValue }) => {
                const fechaNacimiento = getFieldValue('fechaNacimiento');
                const edad = fechaNacimiento ? calcularEdad(fechaNacimiento) : null;
                return (
                  <Form.Item label="Edad">
                    <Input
                      disabled
                      value={edad !== null ? `${edad} años` : '-'}
                      placeholder="Se calculará automáticamente"
                    />
                  </Form.Item>
                );
              }}
            </Form.Item>

            <Form.Item
              name="sexo"
              label="Sexo"
            >
              <Select
                placeholder="Seleccione el sexo"
                allowClear
              >
                <Select.Option value="Masculino">Masculino</Select.Option>
                <Select.Option value="Femenino">Femenino</Select.Option>
                <Select.Option value="Otro">Otro</Select.Option>
              </Select>
            </Form.Item>

            <Form.Item
              name="email"
              label="Email"
              rules={[
                { type: 'email', message: 'El formato del email no es válido' },
              ]}
            >
              <Input placeholder="ejemplo@email.com" />
            </Form.Item>

            <Form.Item
              name="telefono"
              label="Teléfono"
            >
              <Input placeholder="Ingrese el teléfono" />
            </Form.Item>

            <Form.Item
              name="categoria"
              label="Categoría"
            >
              <Select
                placeholder="Seleccione la categoría"
                allowClear
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) =>
                  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
              >
                {categorias.map(cat => (
                  <Select.Option key={cat.cat_id} value={cat.cat_id} label={cat.cat_descripcion}>
                    {cat.cat_descripcion}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              name="activo"
              label="Estado"
              valuePropName="checked"
              initialValue={true}
            >
              <Switch checkedChildren="Activo" unCheckedChildren="Inactivo" />
            </Form.Item>
          </Form>
        </Modal>
      </div>
    </MainLayout>
  );
};

export default AdministracionAlumnos;

